import React, {useState, useContext, useEffect} from 'react';
import {HelmetProvider} from 'react-helmet-async';
import {Routes, Route, Navigate} from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
// ✅ Import AppTheme
import AppTheme from './shared-theme/AppTheme';
import {AuthContext} from './context/AuthContext';
import {Helmet} from 'react-helmet-async';
import './App.css';
import {checkRateLimit} from './utils/api';
import {toast, ToastContainer} from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    clearApplicationBadge,
    getPushConsentStatus,
    initPushNotifications,
    initPushNotificationsWithOptions,
    savePushConsentStatus
} from './utils/pushNotifications';

// Import ProtectedRoute component
import ProtectedRoute from './components/ProtectedRoute';

// Import other components
import ResponsiveNavbar from './components/ResponsiveNavbar';
import ScrollToTop from './components/ScrollToTop';
import HomePage from './pages/HomePage';

import TrainingsPage from './pages/TrainingsPage';
import RecordsPage from './pages/RecordsPage';
import FindUsersPage from './pages/FindUsersPage';
import RegisterTrainingPage from './pages/RegisterTrainingPage';
import MyProfile from './pages/MyProfile';
import LoginForm from "./components/LoginForm";
import JoinUsForm from "./components/JoinUsForm";
import RoleSelectionPage from "./pages/RoleSelectionPage";
import TrainingDiaryPage from "./pages/TrainingDiaryPage";
import AffiliateOwnerPage from "./pages/AffiliateOwnerPage";
import MyAffiliate from "./pages/MyAffiliate";
import Classes from "./pages/Classes";
import Members from "./pages/Members";
import Plans from "./pages/Plans";
import Messages from "./pages/Messages";
import Pricing from "./pages/Pricing";
import MarketingPage from "./pages/MarketingPage";
import Checkout from "./pages/Checkout";
import ShopCheckoutPage from "./pages/ShopCheckoutPage";
import ShopCheckoutFlowPage from "./pages/ShopCheckoutFlowPage";
import EventCheckoutFlowPage from "./pages/EventCheckoutFlowPage";
import Trainer from "./pages/Trainer";
import TrainerSchedulePage from "./pages/TrainerSchedulePage";
import Help from "./pages/Help";
import PrivacyApp from "./pages/Privacy-app"
import PrivacyPolicy from "./pages/PrivacyPolicy";
import VerifyEmail from './components/VerifyEmail';
import ResetPassword from './components/ResetPassword';
import Checkin from './pages/Checkin';

import AfterCheckout from "./pages/AfterCheckout";
import UnsubscribePage from "./pages/UnsubscribePage";
import FollowPage from "./pages/FollowPage";
import StebbyGuide from './pages/StebbyGuide';
import Agreement from "./pages/Agreement";

const HomeRedirect = () => {
    const {isLoggedIn} = useContext(AuthContext);// Assuming you have an auth context with these values
    const userRole = localStorage.getItem("role");
    // Redirect to training diary if logged in as regular user
    if (isLoggedIn && userRole === 'regular') {
        return <Navigate to="/training-diary" replace/>;
    } else if (isLoggedIn && userRole === 'affiliate') {
        return <Navigate to="/affiliate-owner" replace/>;
    }

    // Otherwise, show the normal homepage
    return <HomePage/>;
};

function App() {
    const [isPWA, setIsPWA] = useState(false);
    const [pushConsentDialogOpen, setPushConsentDialogOpen] = useState(false);
    const { isLoggedIn } = useContext(AuthContext);

    useEffect(() => {
        // Check if the app is running in PWA mode
        const checkIfPWA = () => {
            const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
            const isFullscreen = window.matchMedia('(display-mode: fullscreen)').matches;
            const isIosPWA = window.navigator.standalone === true;
            const isAndroidPWA = document.referrer.includes('android-app://');
            return isStandalone || isFullscreen || isIosPWA || isAndroidPWA;
        };

        setIsPWA(checkIfPWA());

        // Listen for display-mode changes
        const mediaQueryList = window.matchMedia('(display-mode: standalone)');
        const handleChange = (e) => {
            setIsPWA(e.matches || checkIfPWA());
        };

        // Add listener
        if (mediaQueryList.addEventListener) {
            mediaQueryList.addEventListener('change', handleChange);
        } else if (mediaQueryList.addListener) { // Support for older browsers
            mediaQueryList.addListener(handleChange);
        }

        // Clean up listener
        return () => {
            if (mediaQueryList.removeEventListener) {
                mediaQueryList.removeEventListener('change', handleChange);
            } else if (mediaQueryList.removeListener) {
                mediaQueryList.removeListener(handleChange);
            }
        };
    }, []);

    const hostname = window.location.hostname;

    useEffect(() => {
        // Kontrolli kas kasutaja oli varem rate-limited
        const rateLimitInfo = checkRateLimit();

        if (rateLimitInfo) {
            // Näita hoiatust, kui limiit pole veel aegunud
            toast.warning(
                <div>
                    <h4>Rate Limit Reached</h4>
                    <p>Some features may be limited for {rateLimitInfo.remainingMinutes} minute(s).</p>
                    <p>Resets at: {rateLimitInfo.expiresAt.toLocaleString()}</p>
                </div>,
                {
                    autoClose: 5000,
                    type: 'warning'
                }
            );
        }
    }, []);

    useEffect(() => {
        // Seadista globaalne toast viide
        window.showRateLimitToast = () => {
            toast.error('Rate limit exceeded! Please wait a few minutes before continuing.', {
                position: "top-center",
                autoClose: 10000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true
            });
        };
    }, []);

    useEffect(() => {
        let cancelled = false;

        const initPushConsentState = async () => {
            if (!isLoggedIn) {
                if (!cancelled) {
                    setPushConsentDialogOpen(false);
                }
                return;
            }

            const permission = typeof Notification !== "undefined" ? Notification.permission : "denied";

            if (permission === "granted") {
                if (!cancelled) {
                    setPushConsentDialogOpen(false);
                }
                await savePushConsentStatus("accepted");
                initPushNotifications();
                return;
            }

            if (permission === "denied") {
                if (!cancelled) {
                    setPushConsentDialogOpen(false);
                }
                await savePushConsentStatus("declined");
                return;
            }

            const consent = await getPushConsentStatus();
            if (!cancelled) {
                setPushConsentDialogOpen(consent !== "declined");
            }
        };

        initPushConsentState();

        return () => {
            cancelled = true;
        };
    }, [isLoggedIn]);

    useEffect(() => {
        if (!isLoggedIn) {
            return;
        }

        clearApplicationBadge();

        const clearOnFocus = () => clearApplicationBadge();
        const clearOnVisible = () => {
            if (document.visibilityState === 'visible') {
                clearApplicationBadge();
            }
        };

        window.addEventListener('focus', clearOnFocus);
        document.addEventListener('visibilitychange', clearOnVisible);

        return () => {
            window.removeEventListener('focus', clearOnFocus);
            document.removeEventListener('visibilitychange', clearOnVisible);
        };
    }, [isLoggedIn]);

    const handleEnablePushNotifications = async () => {
        setPushConsentDialogOpen(false);

        const result = await initPushNotificationsWithOptions({
            requestPermissionOnDefault: true,
        });

        if (result?.ok) {
            await savePushConsentStatus("accepted");
        }
    };

    const handleDeclinePushNotifications = async () => {
        setPushConsentDialogOpen(false);
        await savePushConsentStatus("declined");
    };

    const isNgrokDomain = /(?:^|\.)ngrok-free\.app$|(?:^|\.)ngrok\.app$/i.test(hostname);
    const allowNgrok = process.env.REACT_APP_ALLOW_NGROK === 'true';

    // Check if we're on a subdomain
    const isSubdomain =
        // DEVELOPMENT ONLY: Skip subdomain check for IP addresses (for local network testing)
        // SECURITY: In production, this bypass is disabled
        !(
            (process.env.NODE_ENV !== 'production' && /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/.test(hostname)) ||
            (allowNgrok && isNgrokDomain)
        ) &&
        ((hostname.includes('localhost') && hostname !== 'localhost' && hostname.includes('.')) ||
            (hostname !== 'irontrack.ee' && hostname !== 'www.irontrack.ee' && hostname.split('.').length > 2));

    // If we're on a subdomain, only show the marketing page
    if (isSubdomain) {
        return (
            <HelmetProvider>
                <AppTheme>
                    <CssBaseline/>
                    <Box>
                        <Routes>
                            <Route path="/" element={<MarketingPage/>}/>
                            <Route path="/classes" element={<Classes/>}/>
                            <Route path="/checkin" element={<Checkin/>}/>
                            <Route path="/unsubscribe" element={<UnsubscribePage/>}/>
                            {/* Add other routes as needed */}
                        </Routes>
                    </Box>
                </AppTheme>
            </HelmetProvider>
        );
    }

    return (
        <HelmetProvider>
            <ToastContainer/>

            <AppTheme>
                <CssBaseline/>
                <Helmet>
                    <meta name="viewport"
                          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover"/>
                    {/* PWA-specific meta tags */}
                    {isPWA && (
                        <>
                            <meta name="apple-mobile-web-app-capable" content="yes"/>
                            <meta name="mobile-web-app-capable" content="yes"/>
                        </>
                    )}
                </Helmet>
                <ScrollToTop/>
                <ResponsiveNavbar/>
                <Dialog
                    open={pushConsentDialogOpen}
                    onClose={handleDeclinePushNotifications}
                    maxWidth="xs"
                    fullWidth
                >
                    <DialogTitle>Enable notifications</DialogTitle>
                    <DialogContent>
                        <Typography variant="body2">
                            Notifications are sent by your affiliate (gym), for example schedule updates,
                            announcements, and important member information. Do you want to continue?
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleDeclinePushNotifications}>No</Button>
                        <Button variant="contained" onClick={handleEnablePushNotifications}>
                            Yes
                        </Button>
                    </DialogActions>
                </Dialog>
                <Box
                    sx={{
                        height: '5px',
                        pb: {
                            xs: isLoggedIn ? 'calc(70px + env(safe-area-inset-bottom, 0px))' : 4,
                            md: 4
                        },
                        width: '100%',
                        minHeight: 'calc(100vh - 56px)',
                        overflowX: 'hidden'
                    }}
                >
                    <Routes>
                        {/* Public routes */}
                        <Route path="/" element={<HomeRedirect/>}/>
                        <Route path="/login" element={<LoginForm/>}/>
                        <Route path="/register" element={<JoinUsForm/>}/>
                        <Route path="/pricing" element={<Pricing/>}/>
                        <Route path="/marketing" element={<MarketingPage/>}/>
                        <Route path="/help" element={<Help/>}/>
                        <Route path="/privacy-app" element={<PrivacyApp/>}/>
                        <Route path="/privacy-policy" element={<PrivacyPolicy/>}/>
                        <Route path="/verify-email" element={<VerifyEmail/>}/>
                        <Route path="/reset-password" element={<ResetPassword/>}/>
                        <Route path="/checkin" element={<Checkin/>}/>
                        <Route path="/checkout" element={<Checkout/>}/>
                        <Route path="/shop-checkout" element={<ShopCheckoutPage/>}/>
                        <Route path="/shop-checkout/checkout" element={<ShopCheckoutFlowPage/>}/>
                        <Route path="/event-checkout/checkout" element={<EventCheckoutFlowPage/>}/>
                        <Route path="/after-checkout" element={<AfterCheckout/>}/>
                        <Route path="/unsubscribe" element={<UnsubscribePage/>}/>
                        {/* Protected routes - require authentication */}
                        <Route element={<ProtectedRoute/>}>
                            <Route path="/trainings" element={<TrainingsPage/>}/>
                            <Route path="/records" element={<RecordsPage/>}/>
                            <Route path="/find-users" element={<FindUsersPage/>}/>
                            <Route path="/register-training" element={<RegisterTrainingPage/>}/>
                            <Route path="/my-profile" element={<MyProfile/>}/>
                            <Route path="/select-role" element={<RoleSelectionPage/>}/>
                            <Route path="/training-diary" element={<TrainingDiaryPage/>}/>
                            <Route path="/affiliate-owner" element={<AffiliateOwnerPage/>}/>
                            <Route path="/my-affiliate" element={<MyAffiliate/>}/>
                            <Route path="/classes" element={<Classes/>}/>
                            <Route path="/members" element={<Members/>}/>
                            <Route path="/plans" element={<Plans/>}/>
                            <Route path="/Messages" element={<Messages/>}/>
                            <Route path="/follow" element={<FollowPage/>}/>
                            <Route path="/trainer" element={<Trainer/>}/>
                            <Route path="/trainer-schedule" element={<TrainerSchedulePage/>}/>

                            <Route path="/follow" element={<FollowPage />} />
                            <Route path="/follow/profile/:userId" element={<FollowPage />} />
                            <Route path="/follow/search" element={<FollowPage />} />
                            <Route path="/follow/activity" element={<FollowPage />} />
                            <Route path="/stebby-guide" element={<StebbyGuide />} />
                            <Route path="/agreement" element={<Agreement />} />
                        </Route>
                    </Routes>
                </Box>
            </AppTheme>
        </HelmetProvider>
    );
}

export default App;